/// <reference lib="dom"/>
import * as rxjs from "rxjs";
import type { WbaCtx } from "@wbaudio/core/wbactx";
import { EncodeWav } from "@wbaudio/utils";

export interface LoopPlayerHandlers {
  play: () => void;
  stop: () => void;
}

const SilentSampleRate = 8000;

function SilentWav(duration: number): Blob {
  const length = Math.max(1, Math.round(duration * SilentSampleRate));
  const data = new Float32Array(length);
  return EncodeWav({
    sampleRate: SilentSampleRate,
    numberOfChannels: 1,
    length,
    getChannelData: () => data,
  });
}

export class LoopPlayer {
  readonly wbactx: WbaCtx;
  readonly audio: HTMLAudioElement;
  readonly playing$ = new rxjs.BehaviorSubject(false);
  #buffer: AudioBuffer | null = null;
  #src: AudioBufferSourceNode | null = null;
  #gain: GainNode | null = null;
  #volume = 1;
  #url: string | null = null;

  constructor(wbactx: WbaCtx) {
    this.wbactx = wbactx;
    this.audio = new Audio();
    this.audio.loop = true;
    this.audio.preload = "auto";
    this.audio.addEventListener("playing", () => this.#start());
    this.audio.addEventListener("pause", () => this.#halt());
  }

  get loaded(): boolean {
    return this.#buffer !== null;
  }

  load(buffer: AudioBuffer) {
    this.#buffer = buffer;
    if (this.#url) URL.revokeObjectURL(this.#url);
    this.#url = URL.createObjectURL(SilentWav(buffer.duration));
    this.audio.src = this.#url;
  }

  play(): Promise<void> {
    if (!this.#buffer) return Promise.resolve();
    if (this.wbactx.state["ctx/status"] !== "running") this.wbactx.dispatch({ type: "ctx/resume" });
    return this.audio.play();
  }

  stop() {
    this.audio.pause();
    this.audio.currentTime = 0;
  }

  volume(value: number) {
    this.#volume = Math.min(1, Math.max(0, value));
    if (this.#gain) this.#gain.gain.setTargetAtTime(this.#volume, this.wbactx.now, 0.01);
  }

  session(title: string, artwork: MediaImage[], handlers: LoopPlayerHandlers) {
    if (!("mediaSession" in navigator)) return;
    navigator.mediaSession.metadata = new MediaMetadata({ title, artist: "shusher", artwork });
    navigator.mediaSession.setActionHandler("play", handlers.play);
    navigator.mediaSession.setActionHandler("pause", handlers.stop);
    navigator.mediaSession.setActionHandler("stop", handlers.stop);
  }

  #output(): GainNode {
    if (!this.#gain) {
      this.#gain = this.wbactx.node((c) => c.createGain(), { id: "/loop/gain", name: "loop" });
      this.#gain.gain.value = this.#volume;
      this.#gain.connect(this.wbactx.output);
    }
    return this.#gain;
  }

  #start() {
    if (!this.#buffer || this.#src) return this.#sync(this.#src !== null);
    const ctx = this.wbactx.ctx;
    const src = ctx.createBufferSource();
    src.buffer = this.#buffer;
    src.loop = true;
    src.connect(this.#output());
    src.start(ctx.currentTime);
    this.#src = src;
    this.#sync(true);
  }

  #halt() {
    if (this.#src) {
      this.#src.stop();
      this.#src.disconnect();
    }
    this.#src = null;
    this.#sync(false);
  }

  #sync(playing: boolean) {
    if ("mediaSession" in navigator) {
      navigator.mediaSession.playbackState = playing ? "playing" : "paused";
    }
    if (this.playing$.value !== playing) this.playing$.next(playing);
  }

  dispose() {
    this.stop();
    this.#halt();
    this.audio.removeAttribute("src");
    if (this.#url) URL.revokeObjectURL(this.#url);
    this.#url = null;
    if (this.#gain) this.wbactx.release(this.#gain);
    this.#gain = null;
    this.playing$.complete();
  }
}
